import PropTypes from 'prop-types';
import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { UserManager as Manager } from 'oidc-client';
import { useTranslation } from 'react-i18next';
import Logger from '@medflex/utils/Logger';
import getUserType from '@medflex/utils/getUserType';
import { useHistory, useLocation, Route, Switch } from 'react-router-dom';
import LoadingView from '../LoadingView';
import ErrorView from '../ErrorView';
import SessionExpiredPage from '../SessionExpiredPage';
import UserManagerContext from './UserManagerContext';
import SilentRenew from './SilentRenew';
import SignOut from './SignOut';

const init = ({ ...config }) => new Manager({ ...config });

const isSignInRedirectCallback = location => {
  const searchParams = new URLSearchParams(location.search);

  return Boolean(
    searchParams.get('state') && searchParams.get('code') && searchParams.get('session_state'),
  );
};

const isOidcErrorUrl = location => {
  const searchParams = new URLSearchParams(location.search);

  return Boolean(
    searchParams.get('error') &&
      searchParams.get('error_description') &&
      searchParams.get('state') &&
      searchParams.get('iss'),
  );
};

const isLogoutUrl = location => {
  // /arzt/abmelden is mapped to /abmelden in this context
  return location?.pathname === '/abmelden';
};

const isMagicLinkUrl = location => {
  const searchParams = new URLSearchParams(location.search);

  return Boolean(
    searchParams.get('state') &&
      searchParams.get('code') &&
      searchParams.get('session_state') &&
      searchParams.get('iss') &&
      searchParams.get('kc_action_status') === 'magiclink:login',
  );
};

const cleanQueryString = location => {
  const searchParams = new URLSearchParams(location.search);
  searchParams.delete('state');
  searchParams.delete('session_state');
  searchParams.delete('code');
  searchParams.delete('iss');
  searchParams.delete('error');
  searchParams.delete('error_description');
  searchParams.delete('kc_action_status');
  return `?${searchParams.toString()}`;
};

const GenericUserManager = ({
  children,
  userManagerType,
  basePath,
  silentRedirectUri,
  oidcAuthority,
  clientId,
  extraScopes,
  postLogoutRedirectUri,
  onSessionLoaded,
  onLogout,
  silentRenewDisabled,
  getWebstorageStateStore,
  externalExpired,
}) => {
  const location = useLocation();
  const { t } = useTranslation('components');
  const history = useHistory();
  const [isSessionLoaded, setSessionLoaded] = useState(false);
  const [silentlyRenewing, setSilentlyRenewing] = useState(false);
  const [isExpired, setExpired] = useState(false);
  const [isError, setError] = useState(false);
  const [manager] = useState(
    init({
      // reponse_type: 'code' (as opposed to 'id_token' in the implicit flow) means the authorization
      // endpoint (keycloak) does not directly return the token but a short-living code in the url
      // with which the client can request a token
      response_type: 'code',
      scope: ['openid', 'profile', 'email'].concat(extraScopes).join(' '),
      automaticSilentRenew: false,
      monitorSession: false, // will make the client frequently check for logout events in keycloak. Requires valid frame-src/ancestors in keycloak and messenger webapp.
      filterProtocolClaims: true,
      loadUserInfo: false,
      silent_redirect_uri: silentRedirectUri || `${window.location.origin}${basePath}/silent-renew`,
      authority: oidcAuthority,
      client_id: clientId,
      post_logout_redirect_uri: postLogoutRedirectUri || `${window.location.origin}${basePath}`,
      clockSkew: 6000,
      accessTokenExpiringNotificationTime: 180,
      userStore: getWebstorageStateStore(),
    }),
  );

  // ######################################################################################
  // ## Logout
  // ######################################################################################

  const getIdTokenHint = useCallback(async () => {
    if (!manager) return undefined;
    const user = await manager.getUser();
    if (!user) return undefined;
    return user.id_token;
  }, [manager]);

  const signOut = useCallback(async () => {
    Logger.log(`[${userManagerType}::signOut] - starting user logout`);
    setSilentlyRenewing(false);
    setSessionLoaded(false);

    await manager.clearStaleState();

    await manager.signoutRedirect({
      id_token_hint: await getIdTokenHint(),
      extraQueryParams: {
        client_id: clientId || null,
      },
      post_logout_redirect_uri: postLogoutRedirectUri || null,
    });

    await onLogout();

    Logger.log(`[${userManagerType}::signOut] - finished user logout`);
  }, [clientId, getIdTokenHint, onLogout, manager, postLogoutRedirectUri, userManagerType]);

  const loadSession = useCallback(async () => {
    Logger.log(`[${userManagerType}::loadSession] - Loading Session`);
    const session = await manager.getUser();

    if (!session) {
      Logger.log(`[${userManagerType}::loadSession] - No existing session found`);
      return null;
    }

    return session;
  }, [manager, userManagerType]);

  // ######################################################################################
  // ## Login / Silent Renew
  // ######################################################################################

  const setSession = useCallback(
    session => {
      const userType = getUserType(session.profile.azp);
      Logger.log(`[${userManagerType}::setSession] - userType: ${userType}`);
      Logger.log(`[${userManagerType}::setSession] - expired: ${session.expired}`);
      Logger.log(
        `[${userManagerType}::setSession] - expires: ${new Date(session.expires_at * 1000)}`,
      );
      if (!session.expired) setExpired(false);
      const newSession = { ...session, token_type: 'Bearer', userType };
      onSessionLoaded(newSession);

      setSessionLoaded(true);
    },
    [onSessionLoaded, userManagerType],
  );

  const handleSignInRedirectCallback = useCallback(async () => {
    Logger.log(`[${userManagerType}::handleSignInRedirectCallback] - starting signin redirect`);
    try {
      const user = await manager.signinRedirectCallback();

      setSession(user);
      return history.replace(cleanQueryString(location));
    } catch (e) {
      Logger.error(
        `[${userManagerType}::handleSignInRedirectCallback] -> failed. invalid claim. Remove stale state entries in storage for incomplete authorize requests, remove user, and logout;
        client system time: ${new Date()}, Error: ${e.message}`,
      );
      return signOut();
    }
    // Location and history must not be in the dependencies!
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manager, setSession, signOut, userManagerType]);

  const handleUserManagerLocations = useCallback(async () => {
    if (isOidcErrorUrl(location)) {
      const errorDescription = new URLSearchParams(location.search).get('error_description');
      Logger.error(
        `[${userManagerType}::handleUserManagerLocations] - Got error response from authentication server: ${errorDescription}`,
      );
      setError(true);
      history.replace(cleanQueryString(location));
      return true;
    }

    if (isLogoutUrl(location)) {
      // See Route Definition far below
      Logger.log(`[${userManagerType}::handleUserManagerLocations] - Logout received`);
      return true;
    }

    if (isSignInRedirectCallback(location)) {
      Logger.log(`[${userManagerType}::handleUserManagerLocations] - signInRedirectCallback`);
      await handleSignInRedirectCallback();

      return true;
    }

    if (isMagicLinkUrl(location)) {
      Logger.log(`[${userManagerType}::handleUserManagerLocations] - magic link login`);

      // Convenience check for logging
      const session = await loadSession();

      if (session) {
        // In this case, we need to overwrite the existing access token, by re-loading the session from keycloak
        // If we don't do this, the next token refresh will fail, as keycloak updates the active session and invalidates the old access_token internally
        // The oidc-client library implicitly overwrites the old user in the local storage and correctly replaces it by the new access token
        Logger.info(
          `[${userManagerType}::handleUserManagerLocations] - attempted magic link login with existing session`,
        );
      } else {
        // In this case, we need to login the user using the existing keycloak session. Normally, you would set the session state in the oidc client library and just process the request
        // But there is no non-hacky way to do it using the existing API.
        // Therefore, we just create a new signin request using the OIDC library and throw away the current session information
        // As the latest redirect from keycloak led us to "isMagicUrl" being true, we know that keycloak has a valid session and it will redirect back to the webapp immediately
        // If the session in keycloak was invalid for some reason, the user will see a login page and walk through the flow as if it was not using the magic link at all
        Logger.info(
          `[${userManagerType}::handleUserManagerLocations] - magic link login without existing session`,
        );
      }

      // If we don't clean the query string, we will get into an infinite loop, as keycloak also does not clean them up from the redirect uri
      await manager.signinRedirect({
        redirect_uri:
          window.location.origin + window.location.pathname + cleanQueryString(location),
      });

      return true;
    }

    return false;
    // Location and history must not be in the dependencies!
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userManagerType, handleSignInRedirectCallback, loadSession, manager, setError]);

  const silentlySignIn = useCallback(async () => {
    try {
      Logger.log(`[${userManagerType}::silentlySignIn] - starting silent sign in`);
      await manager.signinSilent();
    } catch (e) {
      Logger.error(
        `[${userManagerType}::silentlySignIn] -> signInSilent failed; trigger signout. ${e.message}`,
      );
      signOut();
    }
  }, [manager, signOut, userManagerType]);

  const signInConditionally = useCallback(async () => {
    const session = await loadSession();

    if (!session) {
      Logger.log(`[${userManagerType}::signInConditionally] - running signIn redirect`);
      return manager.signinRedirect({ redirect_uri: window.location.href });
    }

    // e.g. when loading an offline session from desktop app store
    if (session.expired) {
      Logger.log(`[${userManagerType}::signInConditionally] - running silent signin`);
      return silentlySignIn({ redirect_uri: window.location.href });
    }

    Logger.log(`[${userManagerType}::signInConditionally] - User already has a valid session`);
    setSession(session);

    return null;
  }, [loadSession, silentlySignIn, setSession, manager, userManagerType]);

  // ######################################################################################
  // ## Events
  // ######################################################################################

  const onAccessTokenExpiring = useCallback(() => {
    Logger.log(`[${userManagerType}::onAccessTokenExpiring]`);
  }, [userManagerType]);

  // Only executed, if monitorSession is true (see oidc library docs)
  const onUserSessionChanged = useCallback(
    e => {
      Logger.log(`[${userManagerType}::onUserSessionChanged]`, e);
    },
    [userManagerType],
  );

  // Triggered after successful silent renew
  const onUserLoaded = useCallback(
    user => {
      // "user" is a Keycloak Session Object, containing an id_token, access_token, profile, ...
      Logger.info(`[${userManagerType}::onUserLoaded] ${user?.profile?.userId}`, user);
      // If we don't run setSession here, the manager will have a new access token after silent renew, but the webapp doesn't know
      setSession(user);
    },
    [setSession, userManagerType],
  );

  const onSilentRenewError = useCallback(
    e => {
      const error = {
        ...e,
        message: `[${userManagerType}::onSilentRenewError] - silent renew failed. ${e?.message}`,
      };
      Logger.error(error);
      signOut();
    },
    [signOut, userManagerType],
  );

  const onAccessTokenExpired = useCallback(
    async e => {
      setExpired(true);
      const error = {
        ...e,
        message: `[${userManagerType}::onAccessTokenExpired] - access token expired. ${e?.message}`,
      };
      Logger.warn(error);
    },
    [userManagerType],
  );

  // ######################################################################################
  // ## Effects
  // ######################################################################################

  useEffect(() => {
    if (externalExpired) {
      Logger.warn(
        `[${userManagerType}] - websocket reported expired token; trigger ${'signinSilent'}`,
      );
      Logger.log(`[${userManagerType}] - externalExpired was true`);
      silentlySignIn();
    }
  }, [externalExpired, silentlySignIn, userManagerType]);

  useEffect(() => {
    if (manager && isSessionLoaded) {
      if (silentRenewDisabled && silentlyRenewing) {
        Logger.log(`[${userManagerType}] - stop silent renew`);
        manager.stopSilentRenew();
        setSilentlyRenewing(false);
      }
      if (!silentRenewDisabled && !silentlyRenewing) {
        Logger.log(`[${userManagerType}] - start silent renew`);
        manager.startSilentRenew();
        setSilentlyRenewing(true);
      }
    }
  }, [manager, silentRenewDisabled, isSessionLoaded, silentlyRenewing, userManagerType]);

  useEffect(() => {
    if (manager) {
      manager.events.addUserLoaded(onUserLoaded);
      manager.events.addAccessTokenExpiring(onAccessTokenExpiring);
      manager.events.addAccessTokenExpired(onAccessTokenExpired);
      manager.events.addSilentRenewError(onSilentRenewError);
      manager.events.addUserSessionChanged(onUserSessionChanged);

      return () => {
        manager.events.removeUserLoaded(onUserLoaded);
        manager.events.removeAccessTokenExpiring(onAccessTokenExpiring);
        manager.events.removeAccessTokenExpired(onAccessTokenExpired);
        manager.events.removeSilentRenewError(onSilentRenewError);
        manager.events.removeUserSessionChanged(onUserSessionChanged);
      };
    }
    return null;
  }, [
    manager,
    onAccessTokenExpired,
    onAccessTokenExpiring,
    onSilentRenewError,
    onUserLoaded,
    onUserSessionChanged,
  ]);

  useEffect(() => {
    if (manager)
      (async () => {
        // Check if a special location was accessed. If not, load or login the session.
        if (!(await handleUserManagerLocations())) {
          signInConditionally();
        }
      })();
  }, [handleUserManagerLocations, manager, signInConditionally]);

  // ######################################################################################
  // ## Component
  // ######################################################################################

  const managerValues = useMemo(() => ({ manager, signOutRedirect: signOut }), [manager, signOut]);

  return (
    <UserManagerContext.Provider value={managerValues}>
      <Switch>
        <Route exact path="*/silent-renew" component={SilentRenew} />
        <Route exact path="/abmelden" component={SignOut} />
        <Route>
          {() => {
            if (isExpired) {
              return (
                <SessionExpiredPage
                  title={t('UserManager.SessionExpiredPage.title')}
                  text={t('UserManager.SessionExpiredPage.text')}
                  buttonLabel={t('UserManager.SessionExpiredPage.button_label')}
                  onClick={() => {
                    manager.signinRedirect({
                      redirect_uri: `${window.location.origin}${basePath}`,
                    });
                  }}
                />
              );
            }
            if (isError)
              return (
                <ErrorView
                  {...{
                    title: t('UserManager.ErrorPage.title'),
                    text: t('UserManager.ErrorPage.message'),
                  }}
                  href="#"
                  onClickButton={() => {
                    window.location.reload();
                  }}
                  buttonLabel={t('UserManager.ErrorPage.reload')}
                />
              );
            if (isSessionLoaded) return children;
            return (
              <LoadingView
                title={t('UserManager.LoadingView.title')}
                message={t('UserManager.LoadingView.message')}
              />
            );
          }}
        </Route>
      </Switch>
    </UserManagerContext.Provider>
  );
};

// ######################################################################################
// ## PropTypes
// ######################################################################################

export const userManagerTypes = {
  Browser: 'BrowserUserManager',
  DesktopApp: 'DesktopUserManager',
  MobileApp: 'MobileUserManager',
};

export const oidcClientIds = {
  physician: 'medflex-web-physician',
  patient: 'medflex-web-patient',
  staff: 'medflex-web-staff',
};

GenericUserManager.propTypes = {
  children: PropTypes.node.isRequired,
  basePath: PropTypes.string.isRequired,
  userManagerType: PropTypes.oneOf(Object.values(userManagerTypes)).isRequired,
  oidcAuthority: PropTypes.string.isRequired,
  clientId: PropTypes.oneOf(Object.values(oidcClientIds)).isRequired,
  postLogoutRedirectUri: PropTypes.string.isRequired,
  extraScopes: PropTypes.arrayOf(PropTypes.string),
  onSessionLoaded: PropTypes.func,
  onLogout: PropTypes.func,
  silentRenewDisabled: PropTypes.bool,
  externalExpired: PropTypes.bool,
  silentRedirectUri: PropTypes.string,
  getWebstorageStateStore: PropTypes.func.isRequired,
};

GenericUserManager.defaultProps = {
  onSessionLoaded: () => {},
  onLogout: () => {},
  extraScopes: [],
  silentRedirectUri: '',
  silentRenewDisabled: false,
  externalExpired: false,
};

export default GenericUserManager;
